import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/layout";
import Container from "../components/layout/container";
import { SlidersHorizontal, ChevronDown } from "lucide-react";
import { API } from "../utils/api";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LoadingModal } from "../components/loading";

/* ---------- Reusable UI ---------- */

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-black/10 bg-white shadow-sm ${className}`}
  >
    {children}
  </div>
);

const ProductCard = ({ product, navigate }) => (
  <div
    className="rounded-2xl border border-black/10 bg-white shadow-sm overflow-hidden min-w-0 cursor-pointer"
    onClick={() => navigate(`/product/${product.slug}`)}
  >
    <div className="aspect-[4/3] bg-black/5 overflow-hidden">
      <img
        src={product.img}
        alt={product.title}
        className="h-full w-full object-cover"
        loading="lazy"
      />
    </div>

    <div className="p-4 space-y-1 min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wide text-black/45">
        {product.category}
      </div>

      <div className="line-clamp-2 text-sm font-extrabold text-black/80">
        {product.title}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-extrabold text-orange-600">
          ৳{product.discountPrice ? product.discountPrice : product.basePrice}
        </span>
        {product.discountPrice && (
          <span className="text-xs font-bold text-black/40 line-through">
            ৳{product.basePrice}
          </span>
        )}
      </div>
    </div>
  </div>
);

/* ---------- Page ---------- */

export default function ProductsPage() {
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("All");

  const navigate = useNavigate();

  // const products = useMemo(
  //   () => [
  //     {
  //       id: 1,
  //       category: "HOME & LIVING",
  //       title: "Classic NES Controller Pillow",
  //       price: 29.99,
  //       oldPrice: 39.99,
  //       image:
  //         "https://images.unsplash.com/photo-1606813907291-d86efa9b94db?auto=format&fit=crop&w=1200&q=80",
  //     },
  //     {
  //       id: 2,
  //       category: "DRINKWARE",
  //       title: "Pixel Art Coffee Mug Set",
  //       price: 24.99,
  //       image:
  //         "https://images.unsplash.com/photo-1517701604599-bb29b565090c?auto=format&fit=crop&w=1200&q=80",
  //     },
  //     {
  //       id: 3,
  //       category: "LIGHTING",
  //       title: "Retro Arcade Cabinet Lamp",
  //       price: 49.99,
  //       image:
  //         "https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=1200&q=80",
  //     },
  //     {
  //       id: 4,
  //       category: "WALL ART",
  //       title: "8-Bit Gaming Poster Collection",
  //       price: 34.99,
  //       image:
  //         "https://images.unsplash.com/photo-1520975958225-76888f5d4f57?auto=format&fit=crop&w=1200&q=80",
  //     },
  //     {
  //       id: 5,
  //       category: "ACCESSORIES",
  //       title: "Retro Console Enamel Pins",
  //       price: 12.99,
  //       image:
  //         "https://images.unsplash.com/photo-1520975682031-ae5f9d9edb1b?auto=format&fit=crop&w=1200&q=80",
  //     },
  //     {
  //       id: 6,
  //       category: "APPAREL",
  //       title: "Classic Gaming T-Shirt",
  //       price: 27.99,
  //       image:
  //         "https://images.unsplash.com/photo-1520975687366-6c2d9a73f1f2?auto=format&fit=crop&w=1200&q=80",
  //     },
  //   ],
  //   []
  // );

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/products-get-all`)
      .then((response) => {
        console.log("Fetched products:", response.data);
        setProducts(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    document.title = "Products | Retro Revive";
  }, []);

  useEffect(() => {
    if (categoryFilter === "All") {
      // Fetch all products
      setFilteredProducts(products);
    } else {
      // Filter products by category
      const filtered = products.filter((p) => p.category === categoryFilter);
      setFilteredProducts(filtered);
    }
  }, [categoryFilter, products]);

  const sortedProducts = useMemo(() => {
    let sorted = [...filteredProducts];
    if (sort === "price-asc") {
      sorted.sort((a, b) => a.basePrice - b.basePrice);
    } else if (sort === "price-desc") {
      sorted.sort((a, b) => b.basePrice - a.basePrice);
    } else if (sort === "new") {
      sorted.sort((a, b) => b.pid - a.pid); // Assuming higher ID means newer
    }
    return sorted;
  }, [filteredProducts, sort]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <main className="py-8 overflow-x-hidden">
        {loading && <LoadingModal text="Loading products..." />}
        <Container>
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-black/85 sm:text-3xl">
                Shop Products
              </h1>
              <p className="mt-1 text-sm font-medium text-black/55">
                Browse our curated retro collections
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Mobile filter button */}
              <button className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm font-bold text-black/70 shadow-sm sm:hidden">
                <SlidersHorizontal className="h-4 w-4" />
                Filters
              </button>

              {/* Sort */}
              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="appearance-none rounded-xl border border-black/10 bg-white px-4 py-2 pr-8 text-sm font-bold text-black/70 shadow-sm outline-none"
                >
                  <option value="featured">Featured</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="new">Newest</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-black/50" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-4 min-w-0">
            {/* Filters (desktop) */}
            <aside className="hidden lg:block">
              <Card className="p-4 space-y-4">
                <div className="text-sm font-extrabold text-black/80">
                  Categories
                </div>

                {[
                  "All",
                  "Gift Card",
                  "Home Decor",
                  "Dried Flower",
                  "Soap Flower",
                  "Personalized Gift",
                ].map((c) => (
                  <label
                    key={c}
                    className="flex items-center gap-2 text-sm font-medium text-black/60"
                  >
                    <input
                      type="checkbox"
                      className="accent-orange-600"
                      checked={categoryFilter === c}
                      onChange={() => setCategoryFilter(c)}
                    />
                    {c}
                  </label>
                ))}
              </Card>
            </aside>

            {/* Products */}
            <section className="lg:col-span-3 min-w-0">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-w-0">
                {sortedProducts.map((p) => (
                  <ProductCard key={p.pid} product={p} navigate={navigate} />
                ))}
              </div>
            </section>
          </div>
        </Container>
      </main>
    </Layout>
  );
}
